/* eslint max-statements: ["error", {max: 37}] -- Due to useSelector and message handlers */
/* eslint max-lines-per-function: ["error", {max: 265}]  -- Due to long file */
/* eslint complexity: ["error", {max: 18}] -- Due to long file */
/* eslint max-lines: ["error", {max: 330}] -- Due to long file */
import { useEffect, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import type { AppStateStatus } from "react-native";
import { Alert, AppState } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type {
  WebViewErrorEvent,
  WebViewHttpErrorEvent,
} from "react-native-webview/lib/WebViewTypes";

import { TasksStatusId } from "@/constants/Tasks";
import {
  IncomingWebViewEvent,
  OutgoingWebViewEvent,
} from "@/constants/WebView";
import { router } from "@/crossPlatform/expo-router";
import { WebView } from "@/crossPlatform/WebView";
import type {
  WebViewMessageEvent,
  WebView as WebViewRef,
} from "@/crossPlatform/WebView/types";
import { useToast } from "@/hooks/useToast";
import useAppMonitor from "@/services/AppMonitor";
import { getWebViewAccessTokenFromAuth0 } from "@/services/Auth";
import tasksApi, {
  GetTasksEndpoint,
  useCompleteSurveyTaskMutation,
} from "@/store/api/tasks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { selectTaskDetailsData } from "@/store/reducers/modals/taskDetailsModal";
import { FullScreenSpinner, OpaqueSpinnerOverlay } from "@/ui/core";
import ModalLayout from "@/ui/layout/ModalLayout";
import type { AppUserTaskCache } from "@/utils/api/tasks.util";
import { updateSelectedUserTasks } from "@/utils/api/tasks.util";
import {
  buildSurveyURL,
  getAssessmentViewerRole,
} from "@/utils/assessment.util";
import { toMs } from "@/utils/date.util";
import { isProductionEnvironment } from "@/utils/environment.util";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import {
  isValidWebOrigin,
  postWebViewMessage,
  webViewMessageEventToWebViewMessage,
} from "@/utils/webView.util";

// interval time of 1 minute
const RefreshTokenIntervalTime = toMs({ minutes: 1 });

const Screen = () => {
  const taskDetailsData = useAppSelector(selectTaskDetailsData);
  const { linkedSurveyId, selectedUserId, taskId } = taskDetailsData || {};

  const { captureException, captureMessage } = useAppMonitor();
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { top: topInset } = useSafeAreaInsets();

  const [completeSurveyTask] = useCompleteSurveyTaskMutation();

  const [isSurveyLoading, setIsSurveyLoading] = useState<boolean>(true);
  const [assessmentUrl, setAssessmentUrl] = useState<string | undefined>();
  const [webViewAccessToken, setWebViewAccessToken] = useState<
    string | undefined
  >();
  const getCredentialsInterval = useRef<NodeJS.Timeout | undefined>(undefined);
  const hasRetriedOnLoadingError = useRef<boolean>(false);
  const loadAttempt = useRef<number>(0);
  const webViewRef = useRef<null | WebViewRef>(null);

  const updateWebViewAccessToken = (accessToken: string) => {
    postWebViewMessage({
      message: {
        payload: accessToken,
        type: OutgoingWebViewEvent.AccessTokenUpdated,
      },
      webViewRef,
    });
  };

  const getCredentials = async (): Promise<string> => {
    try {
      const accessToken = await getWebViewAccessTokenFromAuth0();
      setWebViewAccessToken(accessToken);
      return accessToken;
    } catch (error) {
      captureException(
        new Error("failure in assessment webview", { cause: error }),
      );
      throw error;
    }
  };

  const getAssessmentUrl = (token: string) => {
    if (!linkedSurveyId || !token) return;
    setAssessmentUrl(
      buildSurveyURL({
        queryParams: {
          attempt: loadAttempt.current,
          viewerRole: getAssessmentViewerRole(isPatient),
        },
        surveyId: linkedSurveyId,
      }),
    );
  };

  // Refresh token on a 1-minute interval and push updated token to the WebView
  useEffect(() => {
    getCredentialsInterval.current = setInterval(() => {
      void getCredentials().then((token) => {
        updateWebViewAccessToken(token);
      });
    }, RefreshTokenIntervalTime);

    return () => {
      clearInterval(getCredentialsInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch credentials and build the assessment URL on mount
  useEffect(() => {
    void getCredentials().then((token) => {
      getAssessmentUrl(token);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactively refresh token when app becomes active; only push to WebView if token changed
  useEffect(() => {
    const handler = (state: AppStateStatus) => {
      if (state !== "active") return;
      void getCredentials()
        .then((token) => {
          if (!token || token === webViewAccessToken) return;
          updateWebViewAccessToken(token);
        })
        .catch(() => {
          // no-op; fallback retry handles errors
        });
    };
    const sub = AppState.addEventListener("change", handler);
    return () => sub.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webViewAccessToken]);

  if (
    !isNotNilNotEmpty(taskId) ||
    !isNotNilNotEmpty(selectedUserId) ||
    isEmpty(linkedSurveyId)
  ) {
    captureException(
      new Error("AssessmentWebView: opened with missing required task data"),
    );
    if (isWeb()) {
      router.dismiss();
    }
    return <FullScreenSpinner />;
  }

  const handleWebViewMount = () => {
    void getCredentials().then((token) => {
      updateWebViewAccessToken(token);
    });
  };

  const manuallyMarkTaskDone = () => {
    dispatch(
      tasksApi.util.updateQueryData(
        GetTasksEndpoint,
        {
          appUserId,
          isPatient,
          linkedPatients: proxyPatients || [],
        },
        (oldCache: AppUserTaskCache) =>
          updateSelectedUserTasks({
            oldCache,
            selectedUserId,
            taskId,
            taskStatusId: TasksStatusId.Done,
          }),
      ),
    );
  };

  const handleAssessmentCompleted = async () => {
    await completeSurveyTask({ taskId });
    manuallyMarkTaskDone();
    showToast({ text: "Assessment completed", variant: "success" });
    // Dismiss both the webview screen and the assessment-task screen
    router.dismissAll();
  };

  const handleLoadingError = (error?: unknown) => {
    captureException(new Error("Error loading assessment:", { cause: error }));
    setIsSurveyLoading(false);
    Alert.alert("Error loading assessment", "Please try again later", [
      {
        onPress: () => router.dismissAll(),
        text: "Dismiss",
      },
    ]);
  };

  // Retry once after a loading error by refreshing credentials and rebuilding the URL
  // to recover from stale/expired access tokens when the app is backgrounded for a long time
  const handleUnauthorizedLoadingErrorRetry = async () => {
    try {
      setIsSurveyLoading(true);
      const token = await getCredentials();
      loadAttempt.current += 1;
      getAssessmentUrl(token);
    } catch (error) {
      handleLoadingError(error);
    }
  };

  const handleLoadingErrorWithRetry = (error?: unknown) => {
    if (!hasRetriedOnLoadingError.current) {
      hasRetriedOnLoadingError.current = true;
      captureMessage(`Retrying assessment load after error: ${String(error)}`);
      void handleUnauthorizedLoadingErrorRetry();
      return;
    }
    handleLoadingError(error);
  };

  const handleWebViewError = (event: WebViewErrorEvent) => {
    handleLoadingErrorWithRetry(
      new Error(
        `WebView load error: ${event.nativeEvent.description} (code: ${event.nativeEvent.code})`,
      ),
    );
  };

  const handleWebViewHttpError = (event: WebViewHttpErrorEvent) => {
    handleLoadingErrorWithRetry(
      new Error(
        `WebView HTTP error: ${event.nativeEvent.statusCode} - ${event.nativeEvent.url}`,
      ),
    );
  };

  const handleWebViewMessageReceived = (message: WebViewMessageEvent) => {
    if (isWeb() && !isValidWebOrigin(message.nativeEvent.origin)) {
      // on web, only handle messages from the WebAppURL
      return;
    }
    const webViewMessage = webViewMessageEventToWebViewMessage(message);

    if (!webViewMessage) {
      captureMessage("Received invalid WebView message");
      return;
    }

    switch (webViewMessage.type) {
      case IncomingWebViewEvent.AssessmentProgressSaving:
        // no-op — included so it isn't reported as unhandled
        break;
      case IncomingWebViewEvent.Close:
        router.dismiss();
        break;
      case IncomingWebViewEvent.Completed:
        void handleAssessmentCompleted();
        break;
      case IncomingWebViewEvent.Loaded:
        setIsSurveyLoading(false);
        // Reset retry flag after a successful load
        hasRetriedOnLoadingError.current = false;
        break;
      case IncomingWebViewEvent.Loading:
        // no-op — included so it isn't reported as unhandled
        break;
      case IncomingWebViewEvent.LoadingError:
        handleLoadingErrorWithRetry(webViewMessage.payload);
        break;
      case IncomingWebViewEvent.WebViewMounted:
        handleWebViewMount();
        break;
      case OutgoingWebViewEvent.AccessTokenUpdated:
        // no-op — included so it isn't reported as unhandled
        break;
      default:
        captureMessage(
          `Unhandled WebView message: ${message.nativeEvent.data}`,
        );
        break;
    }
  };

  return (
    <ModalLayout isListView shouldBackdropPressDismiss={false}>
      {assessmentUrl ? (
        <WebView
          onError={handleWebViewError}
          onHttpError={handleWebViewHttpError}
          onMessage={handleWebViewMessageReceived}
          ref={webViewRef}
          source={{ uri: assessmentUrl }}
          style={{ flex: 1, paddingTop: topInset }}
          webviewDebuggingEnabled={!isProductionEnvironment()}
        />
      ) : null}
      {isSurveyLoading && <OpaqueSpinnerOverlay />}
    </ModalLayout>
  );
};

export default Screen;
